import { cn } from "../cn";
import type { ProgrammePhase, FunderVisibility, PaymentMethod } from "@/domain/types";

// Shared badge baseline: fixed height, single line — chips must never wrap.
const BADGE = "inline-flex h-[22px] items-center whitespace-nowrap rounded-full px-2 text-[11px] font-medium leading-none";

const PHASE_META: Record<ProgrammePhase, { label: string; cls: string }> = {
  feasibility: { label: "Feasibility", cls: "bg-neutralSoft text-mute" },
  pilot: { label: "Pilot", cls: "bg-infoSoft text-infoStrong" },
  full: { label: "Full programme", cls: "bg-brandSoft text-brand" },
  complete: { label: "Complete", cls: "bg-okSoft text-okStrong" },
};

/** A quiet badge for a programme's lifecycle phase (pilot-readiness addendum §2). */
export function PhaseBadge({ phase, className }: { phase?: ProgrammePhase; className?: string }) {
  if (!phase) return null;
  const m = PHASE_META[phase];
  return <span className={cn(BADGE, m.cls, className)}>{m.label}</span>;
}

/** Funder access state: shared vs being prepared. */
export function VisibilityBadge({ visibility, className }: { visibility?: FunderVisibility; className?: string }) {
  if (!visibility) return null;
  return visibility === "shared" ? (
    <span className={cn(BADGE, "bg-okSoft text-okStrong", className)}>Shared</span>
  ) : (
    <span className={cn(BADGE, "bg-neutralSoft text-mute", className)}>Access pending</span>
  );
}

const PAYMENT_LABEL: Record<PaymentMethod, string> = {
  momo_mtn: "Mobile money (MTN)",
  momo_airtel: "Mobile money (Airtel)",
  bank: "Bank transfer",
  card: "Card",
  cash: "Cash",
};

/** A payment-method chip; cash is visually distinct because it is the rail we work to shrink. */
export function PaymentMethodBadge({ method, className }: { method?: PaymentMethod; className?: string }) {
  if (!method) return null;
  const isCash = method === "cash";
  return (
    <span className={cn(BADGE, isCash ? "bg-warnSoft text-warnStrong" : "bg-neutralSoft text-mute", className)}>
      {PAYMENT_LABEL[method]}
    </span>
  );
}

export function paymentMethodLabel(method: PaymentMethod): string {
  return PAYMENT_LABEL[method];
}
